import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FiLayers } from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import { Header, ButtonLoading, BackButton } from "../components";
import { useStateContext } from "../contexts/ContextProvider";
import useWriteGuard from "../hooks/useWriteGuard";
import { getArmLabel } from "../utils/classGroupHelpers";

const UpdateClass = ({ history }) => {
  const location = useLocation();
  const classId = location.pathname.split("/")[2];

  const { school } = useStateContext();
  const { isWriteBlocked, guardWrite } = useWriteGuard();
  const schoolId = school?._id;

  //const [classes, setClasses] = useState({});
  const [class_name, setClassname] = useState("");
  const [price1, setPrice1] = useState("");
  const [price2, setPrice2] = useState("");
  const [price3, setPrice3] = useState("");
  const [level_of_education, setLevelOfEducation] = useState("");
  const [next_class, setNextClass] = useState("");
  const [is_last_class, setIsLastClass] = useState(false);
  const [classGroupId, setClassGroupId] = useState(null);
  const [groupLabel, setGroupLabel] = useState(null);
  const [classes, setClasses] = useState([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Helper function to check if a field has changed
  const hasChanged = (fieldName, currentValue) => {
    return currentValue !== originalData[fieldName];
  };

  // Get list of changed fields for display
  const getChangedFields = () => {
    const changes = [];
    if (hasChanged("class_name", class_name)) changes.push("Class Name");
    if (
      hasChanged("price1", price1) ||
      hasChanged("price2", price2) ||
      hasChanged("price3", price3)
    )
      changes.push("Class Prices");
    if (hasChanged("level_of_education", level_of_education)) changes.push("Level of Education");
    if (hasChanged("next_class", next_class)) changes.push("Next Class");
    if (hasChanged("is_last_class", is_last_class)) changes.push("Last Class Status");
    return changes;
  };

  // Store original values to track changes
  const [originalData, setOriginalData] = useState({
    class_name: "",
    price1: "",
    price2: "",
    price3: "",
    level_of_education: "",
    next_class: "",
    is_last_class: false,
  });

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const { data } = await userRequest.get(`classes/${schoolId}/${classId}`);
        //console.log(data);

        // Set current values
        setClassname(data.class_name);
        setPrice1(data.class_price["1"]);
        setPrice2(data.class_price["2"]);
        setPrice3(data.class_price["3"]);
        setLevelOfEducation(data.level_of_education);
        setNextClass(data.next_class || "");
        setIsLastClass(data.is_last_class || false);
        setClassGroupId(data.class_group_id || null);
        setGroupLabel(data.group_label || null);

        // Store original values for comparison
        setOriginalData({
          class_name: data.class_name,
          price1: data.class_price["1"],
          price2: data.class_price["2"],
          price3: data.class_price["3"],
          level_of_education: data.level_of_education,
          next_class: data.next_class || "",
          is_last_class: data.is_last_class || false,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAllClasses = async () => {
      try {
        const { data } = await userRequest.get(`classes?code=${school?.code}`);
        setClasses(data.classes || []);
      } catch (error) {
        console.log(error);
      }
    };

    fetchClasses();
    fetchAllClasses();
  }, [schoolId, classId, school?.code]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!guardWrite()) return;
    setLoading(true);

    try {
      // Build request body with only changed fields
      const requestBody = {
        //schoolId
      };

      // Check each field and only include if changed
      if (class_name !== originalData.class_name) {
        requestBody.class_name = class_name;
      }

      if (
        price1 !== originalData.price1 ||
        price2 !== originalData.price2 ||
        price3 !== originalData.price3
      ) {
        requestBody.class_price = {
          1: price1,
          2: price2,
          3: price3,
        };
      }

      if (level_of_education !== originalData.level_of_education) {
        requestBody.level_of_education = level_of_education;
      }

      if (next_class !== originalData.next_class) {
        if (next_class && next_class.trim() !== "") {
          requestBody.next_class = next_class;
          requestBody.is_last_class = false; // If next_class is provided, it can't be the last class
        } else {
          requestBody.next_class = null; // Clear next_class if empty
        }
      }

      if (is_last_class !== originalData.is_last_class) {
        requestBody.is_last_class = is_last_class;
      }

      // Only make the API call if there are actual changes
      if (Object.keys(requestBody).length > 0) {
        await userRequest.put(`classes/${schoolId}/${classId}`, requestBody);
        setSuccess("Class Updated Successfully");

        // Update original data to reflect the new state
        setOriginalData({
          class_name,
          price1,
          price2,
          price3,
          level_of_education,
          next_class,
          is_last_class,
        });
      } else {
        setSuccess("No changes detected");
      }

      setTimeout(() => {
        setSuccess("");
        //history.push("/classes");
      }, 5000);
    } catch (error) {
      console.log(error);
      setError(error.response.data);
      setTimeout(() => {
        setError("");
      }, 5000);
    }
    setLoading(false);
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
      <BackButton history={history} />
      <div className="mb-6">
        <Header category="Set Up" title="Update Class" />
        <p className="text-sm text-slate-500 mt-1">
          Edit class details, fees, and progression settings.
        </p>
      </div>

      {/* Changes Summary */}
      {getChangedFields().length > 0 && (
        <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
          <h4 className="text-sm font-medium text-orange-800 mb-2">Pending Changes</h4>
          <ul className="text-sm text-orange-700 grid gap-2 sm:grid-cols-2">
            {getChangedFields().map((field, index) => (
              <li key={index} className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                {field}
              </li>
            ))}
          </ul>
          <p className="text-xs text-orange-600 mt-2">
            Only the fields listed above will be updated when you save.
          </p>
        </div>
      )}

      {school?.settings?.class_grouping_enabled && classGroupId && (
        <div className="mb-6 p-4 rounded-2xl bg-indigo-50 border border-indigo-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 text-indigo-700 rounded-xl">
              <FiLayers className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-bold text-indigo-900">
                Part of a Class Group{" "}
                {groupLabel ? `(${getArmLabel(school)} ${groupLabel})` : ""}
              </p>
              <p className="text-xs text-indigo-700">
                Setting tuition here creates an arm-specific override. Default tuition can also be
                set for the entire class group.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => history.push(`/class-groups/${classGroupId}`)}
            className="px-3.5 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex-shrink-0"
          >
            Manage Class Group →
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 p-4 md:p-6">
        <form onSubmit={submitHandler} className="space-y-6">
          {error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Name of Class
              {hasChanged("class_name", class_name) && (
                <span className="text-orange-600"> (Modified)</span>
              )}
            </label>
            <input
              type="text"
              onChange={(e) => setClassname(e.target.value)}
              value={class_name}
              className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 ${
                hasChanged("class_name", class_name)
                  ? "border-orange-200 bg-orange-50 focus:border-blue-500"
                  : "border-slate-200 bg-white focus:border-blue-500"
              }`}
              placeholder="e.g. JSS 1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Term Fees</label>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="price1" className="block text-xs font-medium text-slate-500">
                  1st Term
                  {hasChanged("price1", price1) && (
                    <span className="text-orange-600"> (Modified)</span>
                  )}
                </label>
                <input
                  type="number"
                  id="price1"
                  onChange={(e) => setPrice1(e.target.value)}
                  value={price1}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 ${
                    hasChanged("price1", price1)
                      ? "border-orange-200 bg-orange-50 focus:border-blue-500"
                      : "border-slate-200 bg-white focus:border-blue-500"
                  }`}
                  placeholder="20000"
                />
              </div>
              <div>
                <label htmlFor="price2" className="block text-xs font-medium text-slate-500">
                  2nd Term
                  {hasChanged("price2", price2) && (
                    <span className="text-orange-600"> (Modified)</span>
                  )}
                </label>
                <input
                  type="number"
                  id="price2"
                  onChange={(e) => setPrice2(e.target.value)}
                  value={price2}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 ${
                    hasChanged("price2", price2)
                      ? "border-orange-200 bg-orange-50 focus:border-blue-500"
                      : "border-slate-200 bg-white focus:border-blue-500"
                  }`}
                  placeholder="20000"
                />
              </div>
              <div>
                <label htmlFor="price3" className="block text-xs font-medium text-slate-500">
                  3rd Term
                  {hasChanged("price3", price3) && (
                    <span className="text-orange-600"> (Modified)</span>
                  )}
                </label>
                <input
                  type="number"
                  id="price3"
                  onChange={(e) => setPrice3(e.target.value)}
                  value={price3}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 ${
                    hasChanged("price3", price3)
                      ? "border-orange-200 bg-orange-50 focus:border-blue-500"
                      : "border-slate-200 bg-white focus:border-blue-500"
                  }`}
                  placeholder="20000"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Level of Education
                {hasChanged("level_of_education", level_of_education) && (
                  <span className="text-orange-600"> (Modified)</span>
                )}
              </label>
              <select
                onChange={(e) => setLevelOfEducation(e.target.value)}
                value={level_of_education}
                className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 ${
                  hasChanged("level_of_education", level_of_education)
                    ? "border-orange-200 bg-orange-50 focus:border-blue-500"
                    : "border-slate-200 bg-white focus:border-blue-500"
                }`}
              >
                <option value="">Select</option>
                <option value="pre_school">Pre-School</option>
                <option value="kindergarten">Kindergarten</option>
                <option value="nursery">Nursery</option>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Next Class
                {hasChanged("next_class", next_class) && (
                  <span className="text-orange-600"> (Modified)</span>
                )}
              </label>
              <select
                onChange={(e) => setNextClass(e.target.value)}
                value={next_class}
                className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 ${
                  hasChanged("next_class", next_class)
                    ? "border-orange-200 bg-orange-50 focus:border-blue-500"
                    : "border-slate-200 bg-white focus:border-blue-500"
                }`}
              >
                <option value="">Select Next Class</option>
                {classes?.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
              {next_class && (
                <p className="mt-2 text-xs text-blue-600">
                  Students will progress to{" "}
                  {classes?.find((cls) => cls._id === next_class)?.class_name} after completing this
                  class
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <label className="block text-sm font-medium text-slate-800">
                Is Last Class
                {hasChanged("is_last_class", is_last_class) && (
                  <span className="text-orange-600"> (Modified)</span>
                )}
              </label>
              <p className="text-xs text-slate-500 mt-1">
                {is_last_class
                  ? "This is the final class in the progression"
                  : "Students can progress to another class after this"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsLastClass(!is_last_class)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                is_last_class ? "bg-blue-600" : "bg-slate-300"
              } ${hasChanged("is_last_class", is_last_class) ? "ring-2 ring-orange-300" : ""}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  is_last_class ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="flex justify-end gap-2">
            {loading ? (
              <ButtonLoading styles="inline-flex justify-center py-3 px-8 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-indigo-600" />
            ) : (
              <button
                type="submit"
                disabled={isWriteBlocked}
                className="inline-flex justify-center py-3 px-8 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Update Class
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
export default UpdateClass;
